/**
 * Taxonomies Page
 * 
 * Displays a list of taxonomies and a tree editor for managing taxonomy nodes.
 * Supports creating, viewing, and deleting taxonomies.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TreePine, MoreVertical, Trash2, Edit } from 'lucide-react';
import { useI18n } from '@/i18n';
import { 
  Button, 
  Card, 
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui';
import { CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import { CreateTaxonomyModal } from '@/features/knowledge/components/CreateTaxonomyModal';
import { TaxonomyTreeEditor } from '@/features/knowledge/components/TaxonomyTreeEditor';
import { toast } from 'sonner';

// =============================================================================
// Types
// =============================================================================

interface Taxonomy {
  id: string;
  name: string;
  description: string | null;
  children_count: number;
  document_count: number;
  created_at: string;
}

interface TaxonomiesResponse {
  data: Taxonomy[];
  meta?: {
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  };
}

interface DeleteTarget {
  id: string;
  name: string;
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function TaxonomySkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="p-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-3 w-16 mt-2" />
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// Taxonomies Page
// =============================================================================

export function TaxonomiesPage() {
  const { t } = useI18n();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();
  
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTaxonomy, setSelectedTaxonomy] = useState<Taxonomy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const { data: taxonomiesData, isLoading, isError, error, refetch } = useQuery<TaxonomiesResponse>({
    queryKey: ['taxonomies', tenantId],
    queryFn: async (): Promise<TaxonomiesResponse> => {
      // Don't include tenantId in path — apiClient.buildUrl() adds /tenants/{id}/ automatically
      const response = await apiClient.get('/api/v1/taxonomy/roots');
      return response.data as TaxonomiesResponse;
    },
    enabled: !!tenantId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/taxonomy/nodes/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxonomies', tenantId] });
      if (selectedTaxonomy?.id === deleteTarget?.id) {
        setSelectedTaxonomy(null);
      }
      toast.success(`Taxonomy "${deleteTarget?.name}" deleted successfully`);
      setDeleteTarget(null);
    },
    onError: () => {
      toast.error('Failed to delete taxonomy');
    },
  });

  const handleDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
    }
  };

  // Get taxonomies array from response
  const taxonomies = taxonomiesData?.data || (Array.isArray(taxonomiesData) ? taxonomiesData : []);

  // No tenant selected
  if (!tenantId) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <TreePine className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('admin.dashboard.noTenantSelected')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t('admin.dashboard.selectTenantHint')}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Left Panel - Taxonomy List */}
      <div className="w-80 border-r p-4 overflow-y-auto bg-background">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.knowledge.taxonomies.title')}
          </h2>
          <Button size="sm" onClick={() => setShowCreate(true)} leftIcon="plus">
            New
          </Button>
        </div>

        {/* Loading State */}
        {isLoading && <TaxonomySkeleton />}

        {/* Error State */}
        {isError && (
          <Card className="p-4 text-center">
            <p className="text-sm text-red-500 mb-2">
              {error instanceof Error ? error.message : 'Failed to load taxonomies'}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try Again
            </Button>
          </Card>
        )}

        {/* Taxonomies List */}
        {!isLoading && !isError && (
          <div className="space-y-2">
            {taxonomies.map((tax: Taxonomy) => (
              <Card
                key={tax.id}
                className={`cursor-pointer transition-colors hover:shadow-md ${
                  selectedTaxonomy?.id === tax.id 
                    ? 'border-primary ring-1 ring-primary' 
                    : ''
                }`}
                onClick={() => setSelectedTaxonomy(tax)}
              >
                <CardHeader className="p-3 pb-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TreePine className="h-4 w-4 text-muted-foreground" />
                      <CardTitle className="text-sm">{tax.name}</CardTitle>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTaxonomy(tax);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ id: tax.id, name: tax.name });
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <p className="text-xs text-muted-foreground">
                    {tax.children_count} child nodes
                  </p>
                </CardContent>
              </Card>
            ))}

            {taxonomies.length === 0 && (
              <div className="text-center py-8">
                <TreePine className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  No taxonomies yet. Create one to get started.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right Panel - Tree Editor */}
      <div className="flex-1 p-6 overflow-y-auto bg-muted/30">
        {selectedTaxonomy ? (
          <TaxonomyTreeEditor
            tenantId={tenantId}
            taxonomyId={selectedTaxonomy.id}
            taxonomyName={selectedTaxonomy.name}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <TreePine className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg">Select a taxonomy to view and edit its nodes</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateTaxonomyModal
        open={showCreate}
        onOpenChange={setShowCreate}
        tenantId={tenantId}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Taxonomy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? 
              This will permanently remove all nodes in this taxonomy. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? 'Deleting...' : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
